import { doc, getDoc, setDoc } from "firebase/firestore";
import { getDownloadURL, ref } from "firebase/storage";
import { db, storage } from "../firebase";

export type CertificateLogoCache = {
  dataUrl: string;
  sourceLogoUrl: string;
  updatedAt: number;
  width: number;
  height: number;
  mimeType: "image/png";
};

export type CertificateLogoResult = {
  dataUrl: string | null;
  status: "ready" | "missing" | "failed";
  error?: unknown;
};

const MAX_LOGO_WIDTH = 700;
const MAX_LOGO_HEIGHT = 300;
const MIN_LOGO_WIDTH = 180;
const MIN_LOGO_HEIGHT = 80;
const MAX_DATA_URL_LENGTH = 750_000;

const isDirectLogoUrl = (value?: string | null) =>
  Boolean(value && /^https?:\/\//i.test(value));

const getCertificateLogoCacheRef = (clientId: string) =>
  doc(db, "clients", clientId, "certificateAssets", "logo");

const loadImageFromBlob = (blob: Blob) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const objectUrl = URL.createObjectURL(blob);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("certificate-logo-image-load-failed"));
    };
    image.src = objectUrl;
  });

const drawImageToPngDataUrl = (
  image: HTMLImageElement,
  maxWidth: number,
  maxHeight: number,
) => {
  const naturalWidth = image.naturalWidth || image.width;
  const naturalHeight = image.naturalHeight || image.height;

  if (!naturalWidth || !naturalHeight) {
    throw new Error("certificate-logo-invalid-dimensions");
  }

  const scale = Math.min(maxWidth / naturalWidth, maxHeight / naturalHeight, 1);
  const width = Math.max(1, Math.round(naturalWidth * scale));
  const height = Math.max(1, Math.round(naturalHeight * scale));
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  if (!context) {
    throw new Error("certificate-logo-canvas-unavailable");
  }

  canvas.width = width;
  canvas.height = height;
  context.clearRect(0, 0, width, height);
  context.drawImage(image, 0, 0, width, height);

  return {
    dataUrl: canvas.toDataURL("image/png"),
    width,
    height,
  };
};

const createCertificateLogoCache = async (
  sourceLogoUrl: string,
  blob: Blob,
): Promise<CertificateLogoCache> => {
  const image = await loadImageFromBlob(blob);
  let maxWidth = MAX_LOGO_WIDTH;
  let maxHeight = MAX_LOGO_HEIGHT;
  let rendered = drawImageToPngDataUrl(image, maxWidth, maxHeight);

  while (
    rendered.dataUrl.length > MAX_DATA_URL_LENGTH &&
    maxWidth > MIN_LOGO_WIDTH &&
    maxHeight > MIN_LOGO_HEIGHT
  ) {
    maxWidth = Math.max(MIN_LOGO_WIDTH, Math.round(maxWidth * 0.75));
    maxHeight = Math.max(MIN_LOGO_HEIGHT, Math.round(maxHeight * 0.75));
    rendered = drawImageToPngDataUrl(image, maxWidth, maxHeight);
  }

  if (rendered.dataUrl.length > MAX_DATA_URL_LENGTH) {
    throw new Error("certificate-logo-too-large");
  }

  return {
    dataUrl: rendered.dataUrl,
    sourceLogoUrl,
    updatedAt: Date.now(),
    width: rendered.width,
    height: rendered.height,
    mimeType: "image/png",
  };
};

const readFreshCertificateLogoCache = async (
  clientId: string,
  sourceLogoUrl: string,
) => {
  const snapshot = await getDoc(getCertificateLogoCacheRef(clientId));

  if (!snapshot.exists()) return null;

  const data = snapshot.data() as Partial<CertificateLogoCache>;

  if (
    data.sourceLogoUrl !== sourceLogoUrl ||
    typeof data.dataUrl !== "string" ||
    !data.dataUrl.startsWith("data:image/")
  ) {
    return null;
  }

  return data as CertificateLogoCache;
};

const resolveLogoDownloadUrl = async (logoUrl: string) => {
  if (isDirectLogoUrl(logoUrl)) return logoUrl;

  return getDownloadURL(ref(storage, logoUrl));
};

const writeCertificateLogoCache = async (
  clientId: string,
  cache: CertificateLogoCache,
) => {
  await setDoc(getCertificateLogoCacheRef(clientId), cache);
};

export const cacheCertificateLogoFromFile = async (
  clientId: string,
  sourceLogoUrl: string,
  file: File,
) => {
  const cache = await createCertificateLogoCache(sourceLogoUrl, file);
  await writeCertificateLogoCache(clientId, cache);
  return cache;
};

export const getCertificateLogoForClient = async (
  client: Pick<Client, "id" | "logoUrl"> | null | undefined,
): Promise<CertificateLogoResult> => {
  if (!client?.id || !client.logoUrl) {
    return {
      dataUrl: null,
      status: "missing",
    };
  }

  try {
    let cachedLogo: CertificateLogoCache | null = null;

    try {
      cachedLogo = await readFreshCertificateLogoCache(client.id, client.logoUrl);
    } catch (cacheReadError) {
      console.warn("Nie udało się odczytać cache logo do PDF:", cacheReadError);
    }

    if (cachedLogo) {
      return {
        dataUrl: cachedLogo.dataUrl,
        status: "ready",
      };
    }

    const downloadUrl = await resolveLogoDownloadUrl(client.logoUrl);
    const response = await fetch(downloadUrl);

    if (!response.ok) {
      throw new Error("certificate-logo-fetch-failed");
    }

    const cache = await createCertificateLogoCache(
      client.logoUrl,
      await response.blob(),
    );

    try {
      await writeCertificateLogoCache(client.id, cache);
    } catch (cacheError) {
      console.warn("Nie udało się zapisać cache logo do PDF:", cacheError);
    }

    return {
      dataUrl: cache.dataUrl,
      status: "ready",
    };
  } catch (error) {
    return {
      dataUrl: null,
      status: "failed",
      error,
    };
  }
};
